import React, { useState, useEffect, useCallback } from "react";

interface SearchIndex {
  code: string;
  label: string;
}

interface ExternalRecord {
  sourceId: string;
  sourceName: string;
  uniqueId: string;
  title: string | null;
  subtitle: string | null;
  authors: string[];
  publisher: string | null;
  publicationPlace: string | null;
  publicationDate: string | null;
  isbn: string | null;
  issn: string | null;
  language: string | null;
  subjects: string[];
  description: string | null;
  summary: string | null;
  recordType: string | null;
}

interface SearchResult {
  sourceName: string;
  totalResults: number;
  recordsReturned: number;
  hasMoreResults: boolean;
  currentPage: number;
  totalPages: number;
  responseTime: number | null;
  records: ExternalRecord[];
  diagnostics: Record<string, unknown>;
}

interface ExternalSource {
  id: string;
  name: string;
  protocol: string;
  isActive: boolean;
  lastStatus: string | null;
  description: string | null;
}

interface SourcesResponse {
  sources: ExternalSource[];
}

interface SearchResponse {
  error?: string;
  sourceName: string;
  totalResults: number;
  recordsReturned: number;
  hasMoreResults: boolean;
  currentPage: number;
  totalPages: number;
  responseTime: number | null;
  records: ExternalRecord[];
  diagnostics: Record<string, unknown>;
}

interface IsbnSearchResponse {
  found: boolean;
  isbn: string;
  record?: ExternalRecord;
}

interface ImportResponse {
  importData?: Record<string, unknown>;
}

const SEARCH_INDEXES: SearchIndex[] = [
  { code: "any", label: "Tous les champs" },
  { code: "title", label: "Titre" },
  { code: "author", label: "Auteur" },
  { code: "subject", label: "Sujet" },
  { code: "isbn", label: "ISBN" },
  { code: "issn", label: "ISSN" },
  { code: "publisher", label: "Éditeur" },
];

export function ExternalSearchPage(): React.ReactElement {
  const [sources, setSources] = useState<ExternalSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchIndex, setSearchIndex] = useState("any");
  const [results, setResults] = useState<SearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ExternalRecord | null>(null);

  const fetchSources = useCallback(async () => {
    try {
      const response = await fetch("/api/external/sources");
      const data = (await response.json()) as SourcesResponse;
      const sourcesList = data.sources;
      setSources(sourcesList);
      const firstSource = sourcesList[0];
      if (firstSource) {
        setSelectedSource(firstSource.id);
      }
    } catch (err) {
      console.error("Failed to fetch sources:", err);
    }
  }, []);

  // Fetch sources on mount
  useEffect(() => {
    void fetchSources();
  }, [fetchSources]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim() || !selectedSource) return;

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const params = new URLSearchParams({
        q: searchTerm,
        index: searchIndex,
        max: "25",
      });

      const response = await fetch(`/api/external/search/${selectedSource}?${params}`);
      const data = (await response.json()) as SearchResponse;

      if (data.error) {
        setError(data.error);
      } else {
        setResults(data);
      }
    } catch (err) {
      setError("Erreur lors de la recherche");
      console.error("Search error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIsbnSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch(`/api/external/search/isbn/${searchTerm.replace(/[\s-]/g, "")}`);
      const data = (await response.json()) as IsbnSearchResponse;

      if (data.found && data.record) {
        setResults({
          sourceName: data.record.sourceName,
          totalResults: 1,
          recordsReturned: 1,
          hasMoreResults: false,
          currentPage: 1,
          totalPages: 1,
          responseTime: null,
          records: [data.record],
          diagnostics: {},
        });
      } else {
        setError("Aucune notice trouvée pour cet ISBN");
      }
    } catch (err) {
      setError("Erreur lors de la recherche ISBN");
      console.error("ISBN search error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async (record: ExternalRecord) => {
    try {
      const response = await fetch(
        `/api/external/record/${record.sourceName}/${encodeURIComponent(record.sourceId)}/prepare`,
        { method: "POST" }
      );
      const data = (await response.json()) as ImportResponse;

      if (data.importData) {
        // TODO: Open import modal or redirect to catalog creation with pre-filled data
        console.log("Import data:", data.importData);
        alert("Données prêtes pour import. Redirection vers le catalogue...");
      }
    } catch (err) {
      console.error("Import error:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white shadow dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Recherche dans les catalogues externes
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Recherchez des notices bibliographiques dans les catalogues de la BnF, Library of
            Congress et autres sources
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Search Form */}
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <form onSubmit={(e) => void handleSearch(e)} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              {/* Source selector */}
              <div>
                <label
                  htmlFor="source"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Source
                </label>
                <select
                  id="source"
                  value={selectedSource}
                  onChange={(e) => {
                    setSelectedSource(e.target.value);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {sources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Index selector */}
              <div>
                <label
                  htmlFor="index"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Index
                </label>
                <select
                  id="index"
                  value={searchIndex}
                  onChange={(e) => {
                    setSearchIndex(e.target.value);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {SEARCH_INDEXES.map((index) => (
                    <option key={index.code} value={index.code}>
                      {index.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search input */}
              <div className="md:col-span-2">
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Recherche
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    placeholder="Entrez votre recherche..."
                    className="block w-full flex-1 rounded-none rounded-l-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !searchTerm.trim()}
                    className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50 dark:border-gray-600"
                  >
                    {isLoading ? (
                      <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      "Rechercher"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Quick ISBN search */}
            <div className="flex items-center gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">Recherche rapide :</span>
              <button
                type="button"
                onClick={() => void handleIsbnSearch()}
                disabled={isLoading || !searchTerm.trim()}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Rechercher par ISBN dans toutes les sources
              </button>
            </div>
          </form>
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4 dark:bg-red-900/30">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="mt-6">
            {/* Results header */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {results.totalResults} résultat{results.totalResults !== 1 ? "s" : ""} trouvé
                {results.totalResults !== 1 ? "s" : ""}
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  (source : {results.sourceName})
                </span>
              </h2>
              {results.responseTime && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {results.responseTime.toFixed(2)}s
                </span>
              )}
            </div>

            {/* Results list */}
            <div className="space-y-4">
              {results.records.map((record) => (
                <div
                  key={record.uniqueId}
                  className="rounded-lg bg-white p-4 shadow dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {record.title ?? "Sans titre"}
                        {record.subtitle && (
                          <span className="ml-2 font-normal text-gray-600 dark:text-gray-400">
                            : {record.subtitle}
                          </span>
                        )}
                      </h3>

                      {record.authors.length > 0 && (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {record.authors.join(" ; ")}
                        </p>
                      )}

                      <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-500 dark:text-gray-400">
                        {record.publisher && (
                          <span>
                            {record.publisher}
                            {record.publicationDate && `, ${record.publicationDate}`}
                          </span>
                        )}
                        {record.isbn && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-700">
                            ISBN: {record.isbn}
                          </span>
                        )}
                        {record.issn && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-700">
                            ISSN: {record.issn}
                          </span>
                        )}
                        {record.language && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-700">
                            {record.language.toUpperCase()}
                          </span>
                        )}
                        {record.recordType && (
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {record.recordType}
                          </span>
                        )}
                      </div>

                      {record.subjects.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {record.subjects.slice(0, 5).map((subject, idx) => (
                            <span
                              key={idx}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                            >
                              {subject}
                            </span>
                          ))}
                          {record.subjects.length > 5 && (
                            <span className="text-xs text-gray-500">
                              +{record.subjects.length - 5} autres
                            </span>
                          )}
                        </div>
                      )}

                      {record.summary && (
                        <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                          {record.summary}
                        </p>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      <button
                        onClick={() => {
                          setSelectedRecord(record);
                        }}
                        className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        Détails
                      </button>
                      <button
                        onClick={() => void handleImport(record)}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                      >
                        Importer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {results.hasMoreResults && (
              <div className="mt-6 flex justify-center">
                <button className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
                  Charger plus de résultats
                </button>
              </div>
            )}
          </div>
        )}

        {/* Detail modal */}
        {selectedRecord && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/75"
                onClick={() => {
                  setSelectedRecord(null);
                }}
              />

              <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle dark:bg-gray-800">
                <div className="px-4 pt-5 pb-4 sm:p-6">
                  <h3
                    id="modal-title"
                    className="text-lg font-medium text-gray-900 dark:text-white"
                  >
                    {selectedRecord.title}
                  </h3>

                  <dl className="mt-4 space-y-3">
                    {selectedRecord.authors.length > 0 && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Auteur(s)
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRecord.authors.join(" ; ")}
                        </dd>
                      </div>
                    )}

                    {selectedRecord.publisher && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Éditeur
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRecord.publicationPlace &&
                            `${selectedRecord.publicationPlace} : `}
                          {selectedRecord.publisher}
                          {selectedRecord.publicationDate && `, ${selectedRecord.publicationDate}`}
                        </dd>
                      </div>
                    )}

                    {selectedRecord.isbn && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          ISBN
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRecord.isbn}
                        </dd>
                      </div>
                    )}

                    {selectedRecord.description && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Description
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRecord.description}
                        </dd>
                      </div>
                    )}

                    {selectedRecord.summary && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Résumé
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRecord.summary}
                        </dd>
                      </div>
                    )}

                    {selectedRecord.subjects.length > 0 && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Sujets
                        </dt>
                        <dd className="mt-1 flex flex-wrap gap-1">
                          {selectedRecord.subjects.map((subject, idx) => (
                            <span
                              key={idx}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-sm dark:bg-gray-700"
                            >
                              {subject}
                            </span>
                          ))}
                        </dd>
                      </div>
                    )}

                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Source
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {selectedRecord.sourceName} ({selectedRecord.sourceId})
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 dark:bg-gray-700">
                  <button
                    type="button"
                    onClick={() => void handleImport(selectedRecord)}
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                  >
                    Importer cette notice
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRecord(null);
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-gray-600 dark:text-white dark:ring-gray-500"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ExternalSearchPage;
